import { useViewport } from 'contexts/viewport';
import { memo } from 'react';
import { Button, FormLabel, Icon, Paper, Stack, Switch, Text, ViewMore, styled } from 'shell/components';

const FeedbackSection = memo(
    ({
        isEditable,
        form,
        setForm,
        errors,
        setPreviewUploadDialog,
        setFeedbackSettingsDialog,
        isFeedbackSettingsDefault,
        openVideoFeedbackSettings,
    }) => {
        const { device } = useViewport();

        const getText = () => {
            if (form.feedbackSettings.structure === 'rubric') {
                return isFeedbackSettingsDefault(form.feedbackSettings)
                    ? 'Rubric-based feedback (system default)'
                    : 'Rubric-based feedback (Edited)';
            }

            if (form.feedbackSettings.structure === 'system') {
                return 'General & detailed feedback';
            }

            if (form.feedbackSettings.structure === 'templated') {
                return `Smart Feedback Templates ${errors.feedbackSettings || ''}`.trim();
            }

            return '';
        };

        return (
            <Stack sx={{ width: '100%' }} spacing={1}>
                <Text variant={'p1'}>Feedback</Text>
                <CustomPaper variant={'outlined'}>
                    <Stack spacing={4}>
                        <CustomPaper variant={'outlined'}>
                            <Stack spacing={3}>
                                <Stack
                                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                    direction={'row'}
                                    justifyContent={'space-between'}
                                >
                                    <Stack
                                        alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                        direction={device === 'mobile' ? 'column-reverse' : 'row'}
                                        spacing={device === 'mobile' ? 2 : 4}
                                    >
                                        <Text variant={'p1'}>Feedback Settings</Text>
                                        <Button
                                            startIcon={<Icon kind={'video'} />}
                                            variant={'borderless'}
                                            onClick={() => openVideoFeedbackSettings()}
                                        >
                                            Watch a Tutorial
                                        </Button>
                                    </Stack>
                                    <Button
                                        disabled={!isEditable}
                                        variant={'borderless'}
                                        onClick={() => setFeedbackSettingsDialog({ open: true })}
                                    >
                                        Advanced Settings
                                    </Button>
                                </Stack>
                                {form.feedbackSettings.structure === 'custom' ? (
                                    <ViewMore color={'light'} rich text={form.feedbackSettings.customInstructions} />
                                ) : (
                                    <Text color={errors.feedbackSettings ? 'error' : 'light'} rich>
                                        {getText()}
                                    </Text>
                                )}
                            </Stack>
                        </CustomPaper>
                        <Stack spacing={2}>
                            <Stack
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={device === 'mobile' ? 'space-between' : 'initial'}
                                spacing={4}
                            >
                                <FormLabel id={'feedback-switch'}>
                                    <Text variant={'p1'}>Final Submission Feedback Suggestions</Text>
                                </FormLabel>
                                <Switch
                                    aria-labelledby={'feedback-switch'}
                                    checked={form.aiFeedbackEnabled}
                                    disabled={!isEditable}
                                    onChange={(_, value) =>
                                        setForm((current) => ({ ...current, aiFeedbackEnabled: value }))
                                    }
                                />
                            </Stack>
                            <Text>
                                TimelyGrader leverages assignment details to provide instructors with personalized
                                feedback suggestions for student’s final submissions.
                            </Text>
                        </Stack>
                        <Stack spacing={2}>
                            <Stack
                                alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                direction={device === 'mobile' ? 'column' : 'row'}
                                spacing={device === 'mobile' ? 2 : 4}
                            >
                                <Stack
                                    alignItems={'center'}
                                    direction={'row'}
                                    justifyContent={device === 'mobile' ? 'space-between' : 'initial'}
                                    spacing={4}
                                >
                                    <FormLabel id={'student-feedback-switch'}>
                                        <Text variant={'p1'}>Instant Feedback for Students</Text>
                                    </FormLabel>
                                    <Switch
                                        aria-labelledby={'student-feedback-switch'}
                                        checked={form.aiStudentFeedbackEnabled}
                                        disabled={!isEditable || form.feedbackSettings.structure === 'templated'}
                                        onChange={(_, value) =>
                                            setForm((current) => ({ ...current, aiStudentFeedbackEnabled: value }))
                                        }
                                    />
                                </Stack>
                                {form.feedbackSettings.structure === 'templated' && (
                                    <Text color={'light'}>Not available for Smart Feedback Templates</Text>
                                )}
                            </Stack>
                            <Text>
                                Students can generate up to 3 rounds of AI feedback for assignments drafts before they
                                submit. When you enable instant feedback, students will be able to see the assignment
                                description and the assignment rubric.
                            </Text>
                        </Stack>
                        <Button
                            sx={{ alignSelf: 'flex-end' }}
                            disabled={
                                !form.rubric || errors.baseline || form.feedbackSettings.structure === 'templated'
                            }
                            variant={'secondary'}
                            onClick={() => setPreviewUploadDialog({ open: true, target: 'feedback' })}
                        >
                            Preview
                        </Button>
                        {form.feedbackSettings.structure === 'templated' && (
                            <Text align={'right'} color={'light'}>
                                Preview is unavailable for Smart Feedback Templates
                            </Text>
                        )}
                    </Stack>
                </CustomPaper>
            </Stack>
        );
    }
);

const CustomPaper = styled(Paper)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default FeedbackSection;
